/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { formatMetricValue } from "./useDashboardMetrics";
import { IcCheck, IcZap } from "./icons";

/**
 * The metric multi-select grid — live-value cards with a real delta line.
 * Shared by the Add-metrics modal and the New-dashboard wizard. Pass metrics
 * already ranked (rankMetrics). `leading` renders beside the search box
 * (the wizard puts the dashboard-name input there).
 */
export default function MetricPickGrid({ metrics, getSummary, selected, onToggle, leading, query: extQuery, hideSearch }) {
  const [innerQuery, setInnerQuery] = useState("");
  // Controlled by the parent when hideSearch is set (the wizard owns the one
  // shared search box); self-managed otherwise (the Add-metrics modal).
  const query = hideSearch ? (extQuery || "") : innerQuery;

  // Summaries once per data change — not per keystroke or per selection.
  const summaries = useMemo(
    () => new Map((metrics || []).map((m) => [m.id, getSummary(m.id)])),
    [metrics, getSummary]
  );

  const list = (metrics || []).filter(
    (m) => !query || m.title.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      {!hideSearch && (
        <div className={leading ? "db-wiz-row" : undefined}>
          {leading}
          <input
            className="db-msearch"
            placeholder="Search metrics"
            value={innerQuery}
            onChange={(e) => setInnerQuery(e.target.value)}
          />
        </div>
      )}

      <div className="db-mcards">
        {list.length === 0 && (
          <div className="db-mempty" style={{ gridColumn: "1 / -1" }}>
            {(metrics || []).length === 0
              ? "No metrics found yet. Add metrics on the Metrics page first."
              : "No metrics match that search."}
          </div>
        )}
        {list.map((m) => {
          const on = selected.has(m.id);
          const s = summaries.get(m.id) || {};
          const up = (s.deltaPct || 0) >= 0;
          const hasDelta = s.deltaPct !== null && s.deltaPct !== undefined && Number.isFinite(s.deltaPct);
          return (
            <button
              key={m.id}
              type="button"
              className={`db-mcard ${on ? "on" : ""}`}
              aria-pressed={on}
              onClick={() => onToggle(m.id)}
            >
              {on && (
                <span className="db-mcard-check">
                  <IcCheck size={11} />
                </span>
              )}
              <span className="db-mcard-name">
                {m.title}
                {m.gamified && (
                  <span className="db-mcard-game" title="Gamified — live scoring">
                    <IcZap size={11} />
                  </span>
                )}
              </span>
              <span className="db-mcard-val">{formatMetricValue(m, s.total)}</span>
              <span className="db-mcard-foot">
                {hasDelta ? (
                  <span className={`db-mcard-delta ${up ? "up" : "down"}`}>
                    {up ? "\u25b2" : "\u25bc"} {Math.abs(s.deltaPct).toFixed(0)}% {s.periodLabel || "this month"}
                  </span>
                ) : (
                  <span className="db-mcard-delta">{"\u2014"} {s.periodLabel || "this month"}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

MetricPickGrid.propTypes = {
  metrics: PropTypes.array,
  getSummary: PropTypes.func.isRequired,
  selected: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  leading: PropTypes.node,
  query: PropTypes.string,
  hideSearch: PropTypes.bool,
};
